import { useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import { useCreateSession } from "@/api/sessions";
import { useAnalytics } from "@/contexts/analytics-context";
import { type DeliveryMethod } from "@/types/delivery-method";
import { cacheCurrentUrl, removeCachedRedirectUrl } from "@/utils/redirect";

const messages = defineMessages({
  invalidPasswordMessage: {
    id: "modals.sign-in.errors.password",
    defaultMessage: "The password entered does not match our records.",
  },
  invalidUsernameMessage: {
    id: "modals.sign-in.errors.username",
    defaultMessage: "The username entered does not exist.",
  },
});

export interface SignInErrors {
  username?: string;
  password?: string;
}

export interface NewDeviceChallenge {
  challengeId: string;
  deliveryMethods: DeliveryMethod[];
  username: string;
}

interface UseSignInOptions {
  useCurrentUrlForRedirect?: boolean;
  /** Called when the backend requires a 2FA code to continue. */
  onMfaRequired?: (mfaToken: string) => void;
  /** Called when the backend requires verifying a new device. */
  onNewDevice?: (challenge: NewDeviceChallenge) => void;
}

/**
 * Shared username/password sign-in logic used by both the sign-in modal and
 * the inline landing-page form. The caller decides how to surface the
 * MFA / new-device steps via the callbacks.
 */
function useSignIn({
  useCurrentUrlForRedirect = false,
  onMfaRequired,
  onNewDevice,
}: UseSignInOptions = {}) {
  const intl = useIntl();
  const location = useLocation();
  const { track } = useAnalytics();
  const createSession = useCreateSession();

  const [errors, setErrors] = useState<SignInErrors>({});

  const submit = (username: string, password: string) => {
    setErrors({});

    if (useCurrentUrlForRedirect) cacheCurrentUrl(location);

    createSession.mutate(
      { username, password },
      {
        onSettled(_data, error, variables) {
          track("Login Attempt", {
            username: variables.username,
            error: !!error,
            errorMessage: error?.response?.data?.errors
              ? error.response.data.errors[0]?.error_message
              : error?.response?.data?.error,
          });
        },
        onError(error) {
          if (useCurrentUrlForRedirect) removeCachedRedirectUrl();

          const errorCode = error.response?.status;
          switch (errorCode) {
            case 400: // username invalid
              setErrors({
                username: intl.formatMessage(messages.invalidUsernameMessage),
              });
              break;
            case 403: // password invalid or MFA required
              if (error.response?.data.error === "mfa_required") {
                onMfaRequired?.(error.response.data.mfa_token ?? "");
              } else if (
                error.response?.data.error === "security_code_required"
              ) {
                onNewDevice?.({
                  challengeId: error.response.data.challenge_id || "",
                  deliveryMethods:
                    error.response.data.supported_delivery_methods || [],
                  username,
                });
              } else {
                setErrors({
                  password: intl.formatMessage(messages.invalidPasswordMessage),
                });
              }
              break;
          }
        },
      },
    );
  };

  return { submit, errors, setErrors, isLoading: createSession.isLoading };
}

export { useSignIn };
